import React from "react";
import { t } from "@auto/services";

import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import EChart from "@src/reporting/components/EChart";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import {
  isCorrectionFilter,
  reportingResourceKey,
} from "@src/reporting/model/reportingFilters.mjs";
import { dateBucket } from "@src/reporting/model/reportingFormatters.mjs";
import { loadReportingTimeline } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const RegistrationTimelineWidget = ({
  filter,
  refreshToken = 0,
  defaultInterval = "DAY",
  allowIntervalSelection = false,
  compact = false,
}) => {
  const correction = isCorrectionFilter(filter);
  const [interval, setInterval] = React.useState(defaultInterval);

  const resource = useReportingResource(
    () => loadReportingTimeline(filter, interval),
    {
      key: reportingResourceKey("timeline", filter, interval),
      refreshToken,
      initialData: [],
    },
  );

  const rows = resource.data || [];
  const applicantsLabel = t("reporting.applicants");

  const option = React.useMemo(() => ({
    tooltip: { trigger: "axis" },
    grid: { left: 48, right: 20, top: 28, bottom: 36 },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: rows.map((row) => dateBucket(row.bucket, interval)),
    },
    yAxis: { type: "value", minInterval: 1 },
    series: [
      {
        name: applicantsLabel,
        type: "line",
        smooth: true,
        showSymbol: true,
        symbolSize: 5,
        areaStyle: {},
        data: rows.map((row) => row.applicationCount),
      },
    ],
  }), [rows, interval, applicantsLabel]);

  const actions = allowIntervalSelection ? (
    <TextField
      select
      size="small"
      label={t("common.interval")}
      value={interval}
      onChange={(event) => setInterval(event.target.value)}
      className={styles.actionSelect}
    >
      <MenuItem value="DAY">{t("common.day")}</MenuItem>
      <MenuItem value="WEEK">{t("common.week")}</MenuItem>
      <MenuItem value="MONTH">{t("common.month")}</MenuItem>
    </TextField>
  ) : null;

  return (
    <ReportingWidget
      title={correction ? t("reporting.correctionActivity") : t("reporting.registrationActivity")}
      subtitle={correction
        ? t("reporting.correctionTimelineSubtitle")
        : t("reporting.registrationTimelineSubtitle")}
      actions={actions}
      loading={resource.loading}
      error={resource.error}
      empty={resource.hasLoaded && rows.length === 0}
    >
      <EChart
        option={option}
        loading={resource.loading}
        className={compact ? styles.timelineChartCompact : styles.timelineChart}
      />
    </ReportingWidget>
  );
};

export default RegistrationTimelineWidget;
